import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Plus, Search } from 'lucide-react'
import { Phone01Icon, MessageChatCircleIcon } from '@untitledui-icons/react/line'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { CallAssistantModal } from '@/components/voice'
import { useAssistantsList } from '@/hooks/api/useAssistants'
import { usePermissions } from '@/hooks/api'
import { useReferenceMany } from '@/hooks/api/useReference'
import { useListPageSearch } from '@/hooks/useListPageSearch'
import { AssistantFilters } from './components'
import type { Assistant, AssistantListFilters } from '@/types/assistant'

export function AssistantsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()

  // Call modal state
  const [callingAssistant, setCallingAssistant] = useState<Assistant | null>(null)

  // Search and pagination with stable callbacks (prevents focus loss)
  // Note: clearFilters is available from the hook but not used here since
  // AssistantFilters handles clearing via onFiltersChange({})
  const {
    searchQuery,
    handleSearch,
    filters,
    updateFilters,
    hasActiveFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  } = useListPageSearch<AssistantListFilters>({
    useJsonFilter: true,
    debounceMs: 300,
    persistKey: 'assistants', // Persist filters across page navigation
  })

  // Sort state
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'updated_at',
    direction: 'descending',
  })

  // Fetch assistants
  const { data, isLoading, isFetching } = useAssistantsList({
    filters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC',
    },
  })

  const assistants = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Extract IDs for reference lookups
  const notificationUserIds = useMemo(
    () => assistants.map((a) => a.notification_user).filter((id): id is number => id != null),
    [assistants]
  )
  const phoneNumberIds = useMemo(
    () => assistants.map((a) => a.phone_number_id).filter((id): id is number => id != null),
    [assistants]
  )
  const customerIds = useMemo(
    () => assistants.map((a) => a.customer_id).filter((id): id is number => id != null),
    [assistants]
  )

  // Fetch related data using reference hooks
  const { data: usersMap } = useReferenceMany<{ id: number; first_name: string }>(
    'users',
    notificationUserIds,
    { enabled: notificationUserIds.length > 0 }
  )
  const { data: phoneNumbersMap } = useReferenceMany<{ id: number; number: string }>(
    'phone-numbers',
    phoneNumberIds,
    { enabled: phoneNumberIds.length > 0 }
  )
  const { data: customersMap } = useReferenceMany<{ id: number; name: string }>(
    'customers',
    customerIds,
    { enabled: customerIds.length > 0 }
  )

  // Handle assistant click
  const handleAssistantClick = useCallback(
    (assistant: Assistant) => {
      navigate(`/assistants/${assistant.id}/about`)
    },
    [navigate]
  )

  // Handle try assistant
  const handleTryAssistant = useCallback(
    (assistant: Assistant) => {
      navigate(`/assistants/${assistant.id}/try`)
    },
    [navigate]
  )

  // Handle call assistant
  const handleCallAssistant = useCallback((assistant: Assistant) => {
    setCallingAssistant(assistant)
  }, [])

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Handle create assistant
  const handleCreateAssistant = () => {
    navigate('/assistants/create/about')
  }

  // Can create assistants
  const canCreate = can('create:assistants')
  const canViewCustomers = can('view:customers')

  // Check if filters are active (include search query)
  const hasFiltersOrSearch = hasActiveFilters || !!searchQuery

  // Show initial loading only when we have no data at all and no filters
  // (true first load of the page)
  const showInitialLoading = isLoading && !data && !hasFiltersOrSearch

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Show simple empty state only when there's no data and no filters applied
  // (user has never had any assistants)
  if (!data && !hasFiltersOrSearch && assistants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title={t('resources.assistants.no_assistants', { defaultValue: 'No assistants found' })}
          description={t('resources.assistants.no_assistants_hint', { defaultValue: 'Get started by creating your first assistant.' })}
          action={
            canCreate ? (
              <Button onClick={handleCreateAssistant} iconLeading={Plus}>
                {t('resources.assistants.create')}
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  // Unified table layout - always render the same structure to preserve focus
  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('resources.assistants.overview')}
        description={t('resources.assistants.subtitle', { defaultValue: 'Configure and manage your AI assistants' })}
        contentTrailing={
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Search */}
            <Input
              type="text"
              placeholder={t('resources.assistants.search_placeholder', { defaultValue: 'Search...' })}
              value={searchQuery}
              onChange={handleSearch}
              leftIcon={<Search className="h-5 w-5" />}
              className="w-40 sm:w-56"
            />

            {/* Filters */}
            <AssistantFilters filters={filters} onFiltersChange={updateFilters} />

            {/* Create button */}
            {canCreate && (
              <Button onClick={handleCreateAssistant} size="sm" className="sm:size-default" iconLeading={Plus}>
                <span className="hidden sm:inline">{t('resources.assistants.create')}</span>
              </Button>
            )}
          </div>
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      {/* Content area - either table or empty state */}
      {assistants.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            title={t('resources.assistants.no_assistants', { defaultValue: 'No assistants found' })}
            description={t('resources.assistants.no_assistants_hint_filters', { defaultValue: 'Try adjusting your filters or search query.' })}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <Table
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
            aria-label={t('resources.assistants.overview')}
          >
            <Table.Header>
              <Table.Head id="id" label={t('resources.assistants.fields.id', { defaultValue: 'Id' })} allowsSorting isRowHeader />
              <Table.Head id="name" label={t('resources.assistants.fields.name')} allowsSorting />
              <Table.Head id="notification_user" label={t('resources.assistants.fields.notification_user')} />
              <Table.Head id="phone_number_id" label={t('resources.assistants.telephony.phone_number')} />
              {canViewCustomers && (
                <Table.Head id="customer_id" label={t('resources.conversations.filters.customer')} />
              )}
              <Table.Head id="test" label="" />
              <Table.Head id="call" label="" />
            </Table.Header>

            <AriaTableBody>
              {assistants.map((assistant) => {
                const user = assistant.notification_user ? usersMap?.get(assistant.notification_user) : null
                const phone = assistant.phone_number_id ? phoneNumbersMap?.get(assistant.phone_number_id) : null
                const customer = assistant.customer_id ? customersMap?.get(assistant.customer_id) : null

                return (
                  <Table.Row
                    key={assistant.id}
                    id={assistant.id}
                    className="cursor-pointer"
                    onAction={() => handleAssistantClick(assistant)}
                  >
                    {/* ID */}
                    <Table.Cell>
                      <span className="text-sm text-tertiary">{assistant.id}</span>
                    </Table.Cell>

                    {/* Name */}
                    <Table.Cell>
                      <span className="text-sm text-primary">
                        {assistant.name}
                      </span>
                    </Table.Cell>

                    {/* Notification User */}
                    <Table.Cell>
                      {user ? (
                        <span className="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800 dark:bg-brand-900/50 dark:text-brand-200">
                          {user.first_name}
                        </span>
                      ) : null}
                    </Table.Cell>

                    {/* Phone Number */}
                    <Table.Cell>
                      <span className="text-sm text-tertiary">
                        {phone?.number || ''}
                      </span>
                    </Table.Cell>

                    {/* Customer (admin/partner only) */}
                    {canViewCustomers && (
                      <Table.Cell>
                        {customer ? (
                          <Link
                            to={`/customers/${assistant.customer_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
                          >
                            {customer.name}
                          </Link>
                        ) : null}
                      </Table.Cell>
                    )}

                    {/* Test Button */}
                    <Table.Cell>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          handleTryAssistant(assistant)
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <MessageChatCircleIcon className="h-4 w-4" />
                        {t('resources.assistants.actions.test', { defaultValue: 'Test' })}
                      </button>
                    </Table.Cell>

                    {/* Call Button */}
                    <Table.Cell>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          handleCallAssistant(assistant)
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <Phone01Icon className="h-4 w-4" />
                        {t('resources.assistants.actions.call', { defaultValue: 'Call' })}
                      </button>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </AriaTableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}

      {/* Call Assistant Modal */}
      {callingAssistant && (
        <CallAssistantModal
          isOpen={!!callingAssistant}
          onClose={() => setCallingAssistant(null)}
          assistantId={callingAssistant.id}
          assistantName={callingAssistant.name}
        />
      )}
    </TableCard.Root>
  )
}

export default AssistantsPage
